'use client';

import { useEffect, useState } from 'react';
import { stegaClean } from '@sanity/client/stega';

import { cn } from '@/lib/utils';

const LIGHT = {
  box: 'justify-center border-black/[0.12] bg-white/[0.72] py-[1.75rem] backdrop-blur-[0.75rem]',
  label: 'text-black/[0.64]',
  code: 'text-black',
  copy: 'bg-[#0E3B2C]/[0.08] text-[#0E3B2C]',
};

const VARIANTS = {
  final: LIGHT,
  hero: {
    box: 'max-lg:justify-center max-lg:border-black/[0.12] max-lg:bg-white/[0.72] max-lg:py-[1.75rem] max-lg:backdrop-blur-[0.75rem] lg:h-[7.5rem] lg:justify-start lg:border-[#0B1512]/[0.14] lg:bg-white/[0.76] lg:backdrop-blur-[1.6625rem]',
    label: 'max-lg:text-black/[0.64] lg:text-[#0B1512]/[0.64]',
    code: 'max-lg:text-black lg:text-[#0B1512]',
    copy: 'max-lg:bg-[#0E3B2C]/[0.08] max-lg:text-[#0E3B2C] lg:bg-[#0B1512]/[0.10] lg:text-[#0B1512]',
  },
};

export default function CodeBox({
  label,
  code,
  copyLabel,
  variant = 'final',
  className,
}: {
  label?: string;
  code?: string;
  copyLabel?: string;
  variant?: keyof typeof VARIANTS;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);
  const value = stegaClean(code)?.trim();

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  if (!value) return null;

  const colors = VARIANTS[variant];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className={cn('flex items-center gap-[1.5rem] rounded-[1.5rem] border px-[2.5rem]', colors.box, className)}>
      {label && (
        <p className={cn('m-0 whitespace-nowrap font-geist text-[1.75rem] font-[500] leading-[2.5rem]', colors.label)}>
          {label}
        </p>
      )}
      <p className={cn('m-0 whitespace-nowrap font-geist text-[2.25rem] font-[600] leading-[3rem]', colors.code)}>{code}</p>
      {copyLabel && (
        <button
          type="button"
          onClick={copy}
          className={cn(
            'riz-copy relative grid shrink-0 cursor-pointer place-items-center rounded-[0.5rem] px-[1.5rem] py-[0.5rem] font-geist text-[1.75rem] font-[500] leading-[2.5rem]',
            colors.copy,
          )}>
          <span className={cn('[grid-area:1/1] transition-opacity duration-200', copied && 'opacity-0')}>{copyLabel}</span>
          <svg
            viewBox="0 0 32 32"
            aria-hidden
            fill="currentColor"
            className={cn(
              'h-[2.25rem] w-[2.25rem] opacity-0 transition-opacity duration-200 [grid-area:1/1]',
              copied && 'opacity-100',
            )}>
            <path d="M13 24L4 15L5.414 13.586L13 21.171L26.586 7.586L28 9L13 24Z" />
          </svg>
        </button>
      )}
      <style jsx>{`
        .riz-copy {
          transition:
            transform 0.5s cubic-bezier(0.16, 1, 0.3, 1),
            filter 0.3s ease;
        }
        .riz-copy:hover {
          transform: translateY(-0.25rem);
          filter: brightness(1.08);
        }
        .riz-copy:active {
          transform: translateY(0) scale(0.99);
        }
        .riz-copy:focus-visible {
          outline: 2px solid #9fe0b0;
          outline-offset: 2px;
        }
        @media (prefers-reduced-motion: reduce) {
          .riz-copy,
          .riz-copy:hover,
          .riz-copy:active {
            transition: none;
            transform: none;
          }
        }
      `}</style>
    </div>
  );
}
